import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { auth } from '../services/api';
import { Lock, Loader2, ArrowLeft, Eye, EyeOff, CheckCircle2 } from 'lucide-react';

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!token) { setError('Missing reset token. Please request a new link.'); return; }
    if (pw.length < 6) { setError('Password must be at least 6 characters'); return; }
    if (pw !== pw2) { setError('Passwords do not match'); return; }
    setLoading(true);
    try {
      await auth.resetPassword(token, pw);
      setDone(true);
      setTimeout(() => navigate('/login'), 2200);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Reset failed. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center px-4">
      <div className="w-full max-w-md pbb-card p-6 sm:p-8">
        <Link to="/login" className="text-xs text-gray-400 hover:text-white inline-flex items-center gap-1 mb-4"><ArrowLeft size={12} /> Back to login</Link>
        <h1 className="text-2xl font-bold">Reset password</h1>
        <p className="text-sm text-gray-400 mt-1">Pick a new password for your Promote Black Business account.</p>
        {done ? (
          <div className="mt-6 rounded-xl border border-green-500/30 bg-green-500/10 p-5 text-center">
            <CheckCircle2 className="mx-auto text-green-400" size={26} />
            <div className="mt-3 font-semibold text-green-300">Password updated</div>
            <p className="text-sm text-gray-300 mt-1">Redirecting you to login…</p>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <label className="text-xs uppercase tracking-wider text-gray-500 font-semibold flex items-center gap-1"><Lock size={12} /> New password</label>
              <div className="relative mt-1">
                <input data-testid="reset-pw" type={show ? 'text' : 'password'} value={pw} onChange={(e) => setPw(e.target.value)}
                  className="pbb-input pr-10" placeholder="At least 6 characters" required />
                <button type="button" onClick={() => setShow((v) => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white">
                  {show ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Confirm new password</label>
              <input data-testid="reset-pw2" type={show ? 'text' : 'password'} value={pw2} onChange={(e) => setPw2(e.target.value)}
                className="mt-1 w-full bg-black border border-[#1f1f1f] rounded-lg px-3 py-2.5 text-sm" required />
            </div>
            {error && <div className="text-sm text-red-400">{error}</div>}
            <button data-testid="reset-submit" type="submit" disabled={loading || !token}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-green-600 hover:bg-green-500 text-black font-semibold disabled:opacity-50">
              {loading ? <Loader2 className="animate-spin" size={16} /> : null}
              Update password
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
